import type { InstructorSlideDeckCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorSlideDeckCubit'
import type { SectionSelectionCubit } from '@breakoutlearning/firebase-repository/cubits/SectionSelectionCubit'
import { SectionState } from '@breakoutlearning/firebase-repository/types'
import { Spinner } from 'components/Spinner'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import { FormLabel } from 'components/design-system/form/FormLabel'
import { PlusIcon } from 'components/icons/Plus'
import { useRepository } from 'hooks/auth'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { CreateOrEditSectionDialog } from 'pages/instructor/classes/CreateOrEditSectionDialog'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { FieldError } from 'react-hook-form'

export const SectionSelect = observer(function SectionSelect({
  value,
  onChange,
  cubit,
  error,
  hideCompletedSections = true,
}: {
  cubit: InstructorSlideDeckCubit | SectionSelectionCubit
  value?: string
  onChange: (value: string | undefined) => void
  error?: FieldError
  hideCompletedSections?: boolean
}) {
  const sectionId = value

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const createSectionButtonRef = useRef<HTMLButtonElement | null>(null)

  const selectId = useId()
  const { tt } = useTranslationTyped()
  const repository = useRepository()
  const user = useBreakoutUser()
  const section = sectionId
    ? (cubit.sections.value ?? []).find((s) => s.id === sectionId)
    : undefined

  const [filterOnInstructor, setFilterOnInstructor] = useState(
    section?.instructor.id
  )

  const instructorOptions = useMemo(() => {
    const instructorIds = new Set(
      (cubit.sortedSectionOptions.value ?? []).map((s) => s.instructorUserId)
    )
    return Array.from(instructorIds)
      .map((id) => repository.userStore.getUser(id))
      .sort((a, b) => a.fullName.localeCompare(b.fullName))
  }, [cubit.sortedSectionOptions, repository.userStore])

  const sectionOptions = useMemo(() => {
    if (!cubit.sortedSectionOptions.value) return []
    return cubit.sortedSectionOptions.value.filter((section) => {
      // only show sections with dates, regardless of selected value
      if (!section.hasSectionDates) return false

      // if section is unconfigured, hide it (belt and braces)
      if (section.sectionState === SectionState.unconfigured) return false

      // hide completed sections if specified
      if (
        hideCompletedSections &&
        section.sectionState === SectionState.completed
      )
        return false

      // show selected section regardless of filter
      if (section.value === sectionId) return true

      // if filtering, show only sections for that instructor
      return (
        !filterOnInstructor || section.instructorUserId === filterOnInstructor
      )
    })
  }, [
    cubit.sortedSectionOptions,
    filterOnInstructor,
    sectionId,
    hideCompletedSections,
  ])

  useEffect(() => {
    if (!sectionId || !filterOnInstructor) return

    const selectedSection = (cubit.sections.value ?? []).find(
      (s) => s.id === sectionId
    )
    if (
      !selectedSection ||
      selectedSection.instructor.id !== filterOnInstructor
    ) {
      onChange(undefined)
      // form.setValue('sectionId', '', { shouldValidate: true })
    }
  }, [cubit.sections, filterOnInstructor, onChange, sectionId])

  const mySections = cubit.sections

  if (mySections.isLoading) return <Spinner className="mx-auto" />

  return (
    <>
      {instructorOptions.length > 1 && (
        <BreakoutSelect
          inputClassName="h-[50px] mb-1"
          testId="instructor-select"
          kind="secondary"
          label={tt.instructor_library.select_instructor()}
          name="instructorId"
          options={instructorOptions.map((i) => ({
            value: i.id,
            label: i.fullName,
          }))}
          onChange={setFilterOnInstructor}
          value={filterOnInstructor}
        />
      )}
      <BreakoutSelect
        inputClassName="h-[50px]"
        id={selectId}
        value={sectionId}
        testId="section-select"
        error={error}
        required
        kind="secondary"
        label={tt.instructor_library.select_class()}
        onChange={onChange}
        labelComponent={
          <div className="flex w-full flex-row items-center justify-between">
            <FormLabel
              label={tt.instructor_library.select_class()}
              for={selectId}
              required={true}
            />
            {!user.isTA && (
              <div className="w-full px-4 text-body-medium text-on-surface-var">
                <button
                  ref={createSectionButtonRef}
                  className="w-full text-body-medium whitespace-nowrap text-on-surface-var"
                  data-testid="add-new-section-button"
                  onClick={() => {
                    setShowCreateDialog(true)
                  }}
                >
                  <div className="flex items-center justify-end">
                    <PlusIcon
                      size={14}
                      className="mr-0.5 stroke-fixed-accent-color"
                      pathClassName="stroke-fixed-accent-color"
                      pathStrokeWidth={3.5}
                    />
                    {tt.instructor_library.add_new_class()}
                  </div>
                </button>
              </div>
            )}
          </div>
        }
        name="sectionId"
        placeholder={
          !mySections.value.length
            ? tt.instructor_library.no_classes_found()
            : tt.instructor_library.select_class()
        }
        options={sectionOptions}
      />
      {createPortal(
        <CreateOrEditSectionDialog
          open={showCreateDialog}
          inline
          editOrCreate="create"
          createSectionInvoiceRequest={cubit.createSectionInvoiceRequest}
          saveSection={cubit.createSection}
          onCancel={() => {
            setShowCreateDialog(false)
            createSectionButtonRef.current?.focus()
          }}
          onSave={({ sectionId }) => {
            if (!sectionId) return

            onChange(sectionId)

            setShowCreateDialog(false)
            createSectionButtonRef.current?.focus()
          }}
          redeemPromotions={cubit.redeemPromotions}
          applyApprovedFreeAdminPromotion={
            cubit.applyApprovedFreeAdminPromotion
          }
        />,
        document.body
      )}
    </>
  )
})
